import { Button } from "../ui/button";
import { X, Scale } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";

const eur = v => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const eurK = v => `${Math.round((v || 0) / 1000)}k`;
const COL_A = "#f7c935";
const COL_B = "#3b82f6";

const CR_LINES = [
  ["Chiffre d'affaires", "ca"],
  ["EBE", "ebe"],
  ["Résultat net", "resultat_net", true],
  ["CAF", "caf"],
];

function Delta({ a, b }) {
  const d = (b || 0) - (a || 0);
  if (Math.abs(d) < 1) return <span className="text-slate-600">=</span>;
  return <span className={d > 0 ? "text-emerald-400" : "text-rose-400"}>{d > 0 ? "+" : ""}{eur(d)}</span>;
}

export default function CompareView({ a, b, onClose }) {
  const A = a.computed, B = b.computed;

  const kpis = [
    { label: "CA Année 3", va: A.annees[2].ca, vb: B.annees[2].ca },
    { label: "Résultat net An 3", va: A.annees[2].resultat_net, vb: B.annees[2].resultat_net },
    { label: "Point bas trésorerie", va: A.tresorerie.point_bas.solde, vb: B.tresorerie.point_bas.solde },
    { label: "Trésorerie fin An 3", va: A.tresorerie.fin_an3, vb: B.tresorerie.fin_an3 },
  ];

  const chart = A.mensuel.map((m, i) => ({ p: m.period, a: m.solde, b: B.mensuel[i]?.solde }));

  const rnA = A.annees[2].resultat_net, rnB = B.annees[2].resultat_net;
  const winner = rnA === rnB ? null : (rnB > rnA ? b : a);

  return (
    <div className="space-y-4" data-testid="compare-view">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <Scale className="w-5 h-5 text-[#f7c935]" />
        <div className="flex items-center gap-2 flex-wrap flex-1">
          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${COL_A}22`, color: COL_A }} data-testid="compare-name-a">{a.name}</span>
          <span className="text-slate-500 text-xs">vs</span>
          <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: `${COL_B}22`, color: COL_B }} data-testid="compare-name-b">{b.name}</span>
          {winner && (
            <span className="text-[11px] text-slate-400 ml-2" data-testid="compare-verdict">
              → <strong className="text-white">{winner.name}</strong> dégage le meilleur résultat net en année 3 ({eur(Math.max(rnA, rnB))})
            </span>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="compare-close-btn">
          <X className="w-4 h-4 mr-1" /> Fermer la comparaison
        </Button>
      </div>

      {/* KPIs comparés */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="bg-[#14101c] border border-white/5 rounded-xl p-3.5" data-testid={`compare-kpi-${k.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">{k.label}</p>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm font-black tabular-nums" style={{ color: COL_A }}>{eur(k.va)}</span>
              <span className="text-[10px] text-slate-600">/</span>
              <span className="text-sm font-black tabular-nums" style={{ color: COL_B }}>{eur(k.vb)}</span>
            </div>
            <p className="text-[10px] mt-0.5"><Delta a={k.va} b={k.vb} /></p>
          </div>
        ))}
      </div>

      {/* Trésorerie superposée */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
        <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">Solde de trésorerie — 36 mois</p>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="p" tick={{ fill: "#94a3b8", fontSize: 9 }} interval={2} />
              <YAxis tickFormatter={eurK} tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip formatter={v => eur(v)} contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="a" name={a.name} stroke={COL_A} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="b" name={b.name} stroke={COL_B} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Compte de résultat comparé par année */}
      <div className="grid lg:grid-cols-3 gap-3">
        {[0, 1, 2].map(y => (
          <div key={y} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`compare-year-${y + 1}`}>
            <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">Année {y + 1}</p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[10px] uppercase">
                  <th className="text-left py-1.5"></th>
                  <th className="text-right py-1.5" style={{ color: COL_A }}>A</th>
                  <th className="text-right py-1.5" style={{ color: COL_B }}>B</th>
                  <th className="text-right py-1.5 text-slate-500" title="Écart du scénario B par rapport au scénario A">Δ B vs A</th>
                </tr>
              </thead>
              <tbody>
                {CR_LINES.map(([label, key, bold]) => (
                  <tr key={key} className={`border-b border-white/5 ${bold ? "bg-white/[0.03]" : ""}`}>
                    <td className={`py-1.5 pr-1 ${bold ? "font-bold text-white" : "text-slate-300"}`}>{label}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-200">{eur(A.annees[y][key])}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-200">{eur(B.annees[y][key])}</td>
                    <td className="py-1.5 text-right tabular-nums text-[10px]"><Delta a={A.annees[y][key]} b={B.annees[y][key]} /></td>
                  </tr>
                ))}
                <tr>
                  <td className="py-1.5 pr-1 text-slate-300">Trésorerie fin d'année</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-200">{eur(A.tresorerie[`fin_an${y + 1}`])}</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-200">{eur(B.tresorerie[`fin_an${y + 1}`])}</td>
                  <td className="py-1.5 text-right tabular-nums text-[10px]"><Delta a={A.tresorerie[`fin_an${y + 1}`]} b={B.tresorerie[`fin_an${y + 1}`]} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Financement */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-6 flex-wrap" data-testid="compare-financement">
        <p className="text-[10px] uppercase text-slate-500 font-bold">Plan de financement — solde cumulé fin An 3</p>
        <span className="text-sm font-black tabular-nums" style={{ color: COL_A }}>{eur(A.financement[2].solde_cumule)}</span>
        <span className="text-sm font-black tabular-nums" style={{ color: COL_B }}>{eur(B.financement[2].solde_cumule)}</span>
        <span className="text-xs"><Delta a={A.financement[2].solde_cumule} b={B.financement[2].solde_cumule} /></span>
      </div>
    </div>
  );
}
